import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { catchError, firstValueFrom, map, of, timeout } from 'rxjs';

import { environment } from '../../../environments/environment';
import { Response } from '../models/response.model';
import {
  DEFAULT_COLUMN_WIDTH_MAX,
  DEFAULT_COLUMN_WIDTH_MIN,
  setDefaultColumnWidth,
} from '../../util/constants';

interface SystemSettingsPayload {
  settings: {
    default_table_column_width: number;
    help_url?: string | null;
  };
}

@Injectable({ providedIn: 'root' })
export class SystemSettingsService {
  private readonly http = inject(HttpClient);
  private readonly helpUrlState = signal<string | null>(null);
  readonly helpUrl = this.helpUrlState.asReadonly();

  /**
   * The application stays usable with its built-in default if the settings service is unavailable.
   */
  load(): Promise<void> {
    this.helpUrlState.set(null);
    return firstValueFrom(
      this.http.get<Response<SystemSettingsPayload>>(`${environment.baseApiContent}system/settings`).pipe(
        map((response) => {
          this.helpUrlState.set(this.safeHelpUrl(response.data?.settings?.help_url));
          const width = response.data?.settings?.default_table_column_width;
          if (Number.isInteger(width) && width >= DEFAULT_COLUMN_WIDTH_MIN && width <= DEFAULT_COLUMN_WIDTH_MAX) {
            setDefaultColumnWidth(width);
          }
        }),
        timeout(2500),
        catchError(() => of(void 0)),
      ),
    );
  }

  private safeHelpUrl(value: unknown): string | null {
    if (typeof value !== 'string' || value.length > 255 || !/^https?:\/\//i.test(value) || /[\s\u0000-\u001f\u007f\\]/u.test(value)) return null;
    try {
      const url = new URL(value);
      return !url.username && !url.password && url.hostname ? value : null;
    } catch {
      return null;
    }
  }
}
